// Passage de la fiche publique d'une démarche au schéma du moteur de formulaires partagé
// (packages/contrats/src/demarches-moteur-formulaire.ts), tarifs des calculs, erreurs de l'API
// ramenées aux codes des champs, et rendu lisible d'une valeur saisie pour la relecture.
import type { Champ, Colonne, Formulaire } from '@cc/contrats';
import { ErreurApi } from '../../../api/erreurs';
import { date } from '../../../commun/format';
import type { FicheDemarche } from '../types';

export interface ErreurSaisie {
  /** Code du champ, éventuellement suivi d'un chemin (adresse.voie, lignes.0.quantite). */
  champ: string;
  message: string;
}

export function versFormulaire(fiche: Pick<FicheDemarche, 'sections'>): Formulaire {
  return {
    sections: fiche.sections.map((s) => ({
      code: s.code,
      titre: s.titre,
      description: s.description ?? undefined,
      visible_si: s.visible_si ?? undefined,
      champs: s.champs.map(
        (c): Champ => ({
          code: c.code,
          type: c.type as Champ['type'],
          libelle: c.libelle,
          aide: c.aide ?? undefined,
          obligatoire: c.obligatoire,
          valeur_defaut: c.valeur_defaut ?? undefined,
          validation: (c.validation ?? undefined) as Champ['validation'],
          options: c.options ?? undefined,
          colonnes: (c.colonnes ?? undefined) as Colonne[] | undefined,
          type_piece: c.type_piece ?? undefined,
          visible_si: c.visible_si ?? undefined,
          calcul: (c.calcul ?? undefined) as Champ['calcul'],
        }),
      ),
    })),
  };
}

/** Montants connus pour les calculs « référence à un tarif » (montant indicatif de la version). */
export function tarifsDe(fiche: Pick<FicheDemarche, 'tarif'>): Record<string, number> {
  return fiche.tarif.rubrique && fiche.tarif.montant !== null ? { [fiche.tarif.rubrique]: fiche.tarif.montant } : {};
}

/** Erreurs par champ d'une réponse 400, sans le préfixe donnees_formulaire. */
export function erreursFormulaireApi(e: unknown): ErreurSaisie[] {
  if (!(e instanceof ErreurApi)) return [];
  return e.champs.filter((d) => d.champ.startsWith('donnees_formulaire.')).map((d) => ({ champ: d.champ.slice('donnees_formulaire.'.length), message: d.message }));
}

const francs = (n: number) => `${n.toLocaleString('fr-FR')} francs CFA`;

/** Valeur d'un champ en texte lisible (relecture, fiche de suivi). */
export function valeurLisible(champ: Pick<Champ, 'type' | 'options' | 'colonnes'>, valeur: unknown, pieces: { id: string; titre: string }[] = []): string {
  if (valeur === undefined || valeur === null || valeur === '') return 'Non renseigné';
  switch (champ.type) {
    case 'choix_unique':
      return champ.options?.find((o) => o.valeur === valeur)?.libelle ?? String(valeur);
    case 'choix_multiple':
      return Array.isArray(valeur) && valeur.length > 0 ? valeur.map((v) => champ.options?.find((o) => o.valeur === v)?.libelle ?? String(v)).join(', ') : 'Aucun choix';
    case 'oui_non':
      return valeur === true ? 'Oui' : valeur === false ? 'Non' : String(valeur);
    case 'montant':
      return typeof valeur === 'number' ? francs(valeur) : String(valeur);
    case 'nombre':
      return typeof valeur === 'number' ? valeur.toLocaleString('fr-FR') : String(valeur);
    case 'date':
      return typeof valeur === 'string' ? date(valeur) : String(valeur);
    case 'adresse': {
      const a = valeur as Record<string, string | undefined>;
      return [a.voie, a.complement, a.quartier, a.ville].filter((x) => x && x.trim()).join(', ') || 'Non renseignée';
    }
    case 'localisation': {
      const l = valeur as { latitude?: number; longitude?: number };
      return typeof l.latitude === 'number' && typeof l.longitude === 'number' ? `${l.latitude.toFixed(5)}, ${l.longitude.toFixed(5)}` : 'Non renseignée';
    }
    case 'fichier':
      return pieces.find((p) => p.id === valeur)?.titre ?? 'Pièce jointe';
    case 'tableau': {
      const lignes = Array.isArray(valeur) ? valeur : [];
      if (lignes.length === 0) return 'Aucune ligne';
      return lignes
        .map((l, i) => `Ligne ${i + 1} : ${(champ.colonnes ?? []).map((k) => `${k.libelle} ${valeurLisible({ type: k.type, options: k.options }, (l as Record<string, unknown>)[k.code])}`).join(' ; ')}`)
        .join(' | ');
    }
    default:
      return String(valeur);
  }
}
